/* =============================================================================
   warranty-form.js — native Tech-Life warranty ticket request form.
   Page-scoped bundle, loaded only by sections/main-warranty.liquid.

   Recreates the Zoho public form 1:1:
     Step 1  Contact info      -> real Zoho field names (posted verbatim)
     Step 2  Product details   -> repeatable entries, branched by dealer Yes/No
     Step 3  Review + notes    -> product entries flattened into MultiLine2

   Zoho's htmlRecords endpoint accepts flat fields only (no subform records),
   so every product entry is serialized into the "Additional Notes" multiline
   field. Contact fields keep their native Zoho name= attributes, which means
   the form still submits correctly with JavaScript disabled.

   Exposes window.TL.WarrantyForm for debugging; auto-boots on DOMContentLoaded.
   ============================================================================= */
(function () {
  'use strict';

  window.TL = window.TL || {};

  var MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  /* ---------- small helpers ---------------------------------------------- */

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function reduceMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function scrollTo(el) {
    if (!el) return;
    el.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
  }

  /** Open/close a .wf__reveal wrapper. */
  function reveal(el, open) {
    if (!el) return;
    el.classList.toggle('is-open', !!open);
    el.setAttribute('aria-hidden', open ? 'false' : 'true');
    // Keep closed inputs out of the tab order and out of validation.
    $$('input, select, textarea', el).forEach(function (f) {
      f.disabled = !open;
    });
  }

  /** ISO yyyy-mm-dd (native date input) -> Zoho's MMMM-dd-yyyy. */
  function toZohoDate(iso) {
    if (!iso) return '';
    var p = String(iso).split('-');
    if (p.length !== 3) return iso;
    var m = parseInt(p[1], 10);
    if (!m || m < 1 || m > 12) return iso;
    return MONTHS[m - 1] + '-' + p[2] + '-' + p[0];
  }

  function digitsOnly(v) { return String(v || '').replace(/\D+/g, ''); }

  /** 10 digits -> ###-###-#### (partial input formats as far as it can). */
  function formatPhone(v) {
    var d = digitsOnly(v).slice(0, 10);
    if (d.length > 6) return d.slice(0, 3) + '-' + d.slice(3, 6) + '-' + d.slice(6);
    if (d.length > 3) return d.slice(0, 3) + '-' + d.slice(3);
    return d;
  }

  function isEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v || '').trim());
  }

  /* ---------- field-level validation -------------------------------------- */

  function fieldOf(input) { return input.closest('.field') || input.closest('.wf__fieldgroup'); }

  function setError(input, message) {
    var wrap = fieldOf(input);
    if (!wrap) return;
    wrap.classList.add('field--error');
    var err = $('.field__error', wrap);
    if (err && message) err.textContent = message;
    input.setAttribute('aria-invalid', 'true');
  }

  function clearError(input) {
    var wrap = fieldOf(input);
    if (!wrap) return;
    wrap.classList.remove('field--error');
    input.removeAttribute('aria-invalid');
  }

  /**
   * Validate one control against its data-* rules.
   * Returns true when valid. Never validates disabled (collapsed) controls.
   */
  function validateControl(input) {
    if (input.disabled || input.type === 'hidden') return true;

    var val = (input.value || '').trim();
    var required = input.hasAttribute('data-required');
    var kind = input.getAttribute('data-validate') || '';

    if (required && !val) {
      setError(input, input.getAttribute('data-msg-required') || 'This field is required.');
      return false;
    }
    if (val && kind === 'email' && !isEmail(val)) {
      setError(input, 'Enter a valid email address.');
      return false;
    }
    if (val && kind === 'phone' && digitsOnly(val).length !== 10) {
      setError(input, 'Enter a complete 10-digit phone number.');
      return false;
    }
    if (val && kind === 'digits') {
      var need = parseInt(input.getAttribute('maxlength'), 10) || 0;
      if (digitsOnly(val).length !== need) {
        setError(input, 'Enter a complete phone number.');
        return false;
      }
    }
    clearError(input);
    return true;
  }

  /** Validate a whole container. Returns the first invalid control, or null. */
  function validateScope(scope) {
    var first = null;
    $$('input, select, textarea', scope).forEach(function (f) {
      if (!validateControl(f) && !first) first = f;
    });
    return first;
  }

  /* =========================================================================
     Radio-group validation (the dealer Yes/No) is handled separately because
     the group, not the individual input, owns the error slot.
     ========================================================================= */
  function validateRadioGroup(scope, name) {
    var inputs = $$('input[type="radio"][name="' + name + '"]', scope);
    if (!inputs.length) return true;
    var group = inputs[0].closest('.wf__fieldgroup');
    var picked = inputs.some(function (i) { return i.checked; });
    if (group) {
      group.classList.toggle('field--error', !picked);
      var err = $('.field__error', group);
      if (err && !picked) err.textContent = 'Please choose Yes or No.';
    }
    return picked;
  }

  /* =========================================================================
     WarrantyForm
     ========================================================================= */
  function WarrantyForm(root) {
    this.root = root;
    this.form = $('[data-wf-form]', root);
    if (!this.form) return;

    this.maxProducts = parseInt(root.getAttribute('data-max-products'), 10) || 10;
    this.submitMode = root.getAttribute('data-submit-mode') || 'iframe';

    this.steps = $$('[data-wf-step]', root);
    this.rail = $$('[data-wf-rail]', root);
    this.productsEl = $('[data-wf-products]', root);
    this.addBtn = $('[data-wf-add]', root);
    this.limitNote = $('[data-wf-limit]', root);
    this.alertEl = $('[data-wf-alert]', root);
    this.successEl = $('[data-wf-success]', root);
    this.reviewEl = $('[data-wf-review]', root);
    this.notesEl = $('[data-wf-notes]', root);
    this.sink = $('[data-wf-sink]', root);
    this.submitBtn = $('[data-wf-submit]', root);
    this.shipEl = $('[data-reveal="ship"]', root);

    // Templates sit as siblings of the section root, so fall back to document.
    this.templates = {
      No: $('#wf-tpl-consumer', root) || document.getElementById('wf-tpl-consumer'),
      Yes: $('#wf-tpl-dealer', root) || document.getElementById('wf-tpl-dealer')
    };

    this.mode = null;      // 'Yes' | 'No'
    this.uid = 0;          // unique id counter for label/for pairs
    this.current = 0;      // active step index
    this.rawNotes = '';    // notes text before the product block is prepended

    this.bind();
    this.initPhone();
    // The shipping address only applies to non-dealers — stays closed (and out
    // of validation) until the dealer question is answered "No".
    reveal(this.shipEl, false);
    this.go(0);
  }

  WarrantyForm.prototype.bind = function () {
    var self = this;

    /* --- step headers: click a completed step to reopen it --------------- */
    this.steps.forEach(function (step, i) {
      var head = $('[data-wf-step-head]', step);
      if (!head) return;
      head.addEventListener('click', function () {
        if (step.classList.contains('is-done')) self.go(i);
      });
      head.addEventListener('keydown', function (ev) {
        if (ev.key !== 'Enter' && ev.key !== ' ' && ev.key !== 'Spacebar') return;
        ev.preventDefault();
        if (step.classList.contains('is-done')) self.go(i);
      });
    });

    /* --- next / back ------------------------------------------------------ */
    $$('[data-wf-next]', this.root).forEach(function (btn) {
      btn.addEventListener('click', function () { self.next(); });
    });
    $$('[data-wf-back]', this.root).forEach(function (btn) {
      btn.addEventListener('click', function () { self.go(self.current - 1); });
    });


    /* --- clear a field's error as soon as the user fixes it --------------- */
    this.form.addEventListener('input', function (e) {
      var t = e.target;
      if (t.matches('input, select, textarea')) {
        var wrap = fieldOf(t);
        if (wrap && wrap.classList.contains('field--error')) validateControl(t);
      }
    });
    this.form.addEventListener('change', function (e) {
      var t = e.target;
      if (t.matches('select')) t.setAttribute('data-empty', t.value ? 'false' : 'true');
      if (t.matches('input[type="radio"][name="Radio"]')) {
        validateRadioGroup(self.form, 'Radio');
        self.onDealerChange(t.value);
      }
    });

    /* --- product-entry delegation ----------------------------------------- */
    if (this.productsEl) {
      this.productsEl.addEventListener('change', function (e) {
        var row = e.target.closest('[data-wf-product]');
        if (row) self.syncRow(row);
      });
      this.productsEl.addEventListener('input', function (e) {
        var row = e.target.closest('[data-wf-product]');
        if (row) self.rowTitle(row);
      });
      this.productsEl.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-wf-remove]');
        if (!btn) return;
        var row = btn.closest('[data-wf-product]');
        if (row) { row.remove(); self.renumber(); }
      });
    }

    if (this.addBtn) {
      this.addBtn.addEventListener('click', function () {
        var row = self.addRow();
        if (row) scrollTo(row);
      });
    }

    /* --- submit ----------------------------------------------------------- */
    this.form.addEventListener('submit', function (e) { self.onSubmit(e); });
  };

  /* ---------- phone --------------------------------------------------------
     The visitor types one number; Zoho's field is a three-part control, so the
     hidden countrycode/first/second inputs are created here and kept in sync.
     (With JS off the <noscript> three-box fallback posts them directly.)
     ------------------------------------------------------------------------ */
  WarrantyForm.prototype.initPhone = function () {
    var self = this;
    var wrap = $('[data-wf-phone]', this.root);
    var input = wrap && $('[data-wf-phone-input]', wrap);
    if (!input) return;

    this.phoneEl = input;
    this.phoneParts = ['PhoneNumber_countrycode', 'PhoneNumber_first', 'PhoneNumber_second']
      .map(function (name) {
        var h = document.createElement('input');
        h.type = 'hidden';
        h.name = name;
        h.setAttribute('checktype', 'c7');
        h.setAttribute('fieldType', '11');
        h.setAttribute('phoneFormat', '2');
        wrap.appendChild(h);
        return h;
      });

    input.addEventListener('input', function () {
      // Reformat only while typing at the end, so editing mid-number and
      // backspacing over a dash don't fight the caret.
      if (input.selectionStart === input.value.length) input.value = formatPhone(input.value);
      self.syncPhone();
    });
    input.addEventListener('blur', function () {
      input.value = formatPhone(input.value);
      self.syncPhone();
    });
    this.syncPhone();
  };

  WarrantyForm.prototype.syncPhone = function () {
    if (!this.phoneParts) return;
    var d = digitsOnly(this.phoneEl.value).slice(0, 10);
    this.phoneParts[0].value = d.slice(0, 3);
    this.phoneParts[1].value = d.slice(3, 6);
    this.phoneParts[2].value = d.slice(6, 10);
  };

  /* ---------- dealer branch ------------------------------------------------ */

  WarrantyForm.prototype.onDealerChange = function (value) {
    // Non-dealers ship the speaker to us and need it back — dealers don't.
    reveal(this.shipEl, value === 'No');
    if (value === this.mode) return;
    var hadEntries = this.productsEl && this.productsEl.children.length > 0;
    this.mode = value;
    if (hadEntries) {
      // The two branches ask different questions — start the entries over.
      this.productsEl.innerHTML = '';
      this.addRow();
      this.notice(
        'Product questions changed because the dealer answer changed. Please re-enter your product details.'
      );
    }
    var band = $('[data-wf-mode-note]', this.root);
    if (band) {
      band.textContent = value === 'Yes'
        ? 'Dealer entries: log the sale date and, optionally, your customer’s contact details.'
        : 'Add each speaker that needs warranty service. Use “Add another product” for additional items.';
    }
  };

  /* ---------- product rows ------------------------------------------------- */

  WarrantyForm.prototype.addRow = function () {
    if (!this.productsEl || !this.mode) return null;
    if (this.productsEl.children.length >= this.maxProducts) return null;

    var tpl = this.templates[this.mode];
    if (!tpl) return null;

    var frag = tpl.content.cloneNode(true);
    var row = frag.querySelector('[data-wf-product]');
    var n = ++this.uid;

    // Unique ids so every <label for> still points at exactly one control.
    $$('[id]', frag).forEach(function (el) {
      var old = el.id;
      var neu = old + '-' + n;
      el.id = neu;
      var lbl = frag.querySelector('label[for="' + old + '"]');
      if (lbl) lbl.setAttribute('for', neu);
      var desc = frag.querySelector('[aria-describedby="' + old + '"]');
      if (desc) desc.setAttribute('aria-describedby', neu);
    });

    // Collapsed reveals start disabled so they never block validation.
    $$('.wf__reveal', frag).forEach(function (r) {
      if (!r.classList.contains('is-open')) reveal(r, false);
    });

    this.productsEl.appendChild(frag);
    this.renumber();
    this.syncRow(row);
    return row;
  };

  WarrantyForm.prototype.renumber = function () {
    var rows = $$('[data-wf-product]', this.productsEl);
    rows.forEach(function (row, i) {
      var n = $('[data-wf-product-n]', row);
      if (n) n.textContent = 'Product ' + (i + 1);
    });
    this.productsEl.setAttribute('data-count', rows.length);
    if (this.addBtn) this.addBtn.hidden = rows.length >= this.maxProducts;
    if (this.limitNote) {
      this.limitNote.hidden = rows.length < this.maxProducts;
      this.limitNote.textContent =
        'Maximum ' + this.maxProducts + ' products per ticket. Submit this one and start another for more.';
    }
    if (!rows.length) this.addRow();
  };

  /** Apply the in-row progressive disclosure + conditional follow-ups. */
  WarrantyForm.prototype.syncRow = function (row) {
    if (!row) return;
    var get = function (key) { return $('[data-f="' + key + '"]', row); };

    // 1. The rest of the entry unlocks once model + problem are chosen.
    var model = get('model');
    var problem = get('problem');
    var rest = $('[data-reveal="rest"]', row);
    var unlocked = !!(model && model.value && problem && problem.value);
    if (rest) reveal(rest, unlocked);

    // 2. Purchase channel follow-ups (consumer branch only).
    var channel = get('channel');
    if (channel) {
      var isDealerChannel = unlocked && channel.value === 'Tool Dealer';
      var isOther = unlocked && channel.value === 'Other';
      reveal($('[data-reveal="tool-dealer"]', row), isDealerChannel);
      reveal($('[data-reveal="other"]', row), isOther);
    }

    row.classList.toggle('is-incomplete', !!validateScopeQuiet(row));
    this.rowTitle(row);
  };

  /** Non-destructive completeness probe — no error styling applied. */
  function validateScopeQuiet(scope) {
    var missing = null;
    $$('[data-required]', scope).forEach(function (f) {
      if (f.disabled) return;
      if (!(f.value || '').trim() && !missing) missing = f;
    });
    return missing;
  }

  WarrantyForm.prototype.rowTitle = function (row) {
    var title = $('[data-wf-product-title]', row);
    if (!title) return;
    var model = $('[data-f="model"]', row);
    var problem = $('[data-f="problem"]', row);
    var parts = [];
    if (model && model.value) parts.push(model.value);
    if (problem && problem.value) parts.push(problem.value);
    title.textContent = parts.length ? parts.join(' — ') : 'New product entry';
  };

  /* ---------- step machine -------------------------------------------------- */

  WarrantyForm.prototype.go = function (index) {
    var self = this;
    if (index < 0 || index >= this.steps.length) return;
    this.current = index;

    this.steps.forEach(function (step, i) {
      var done = i < index;
      step.classList.toggle('is-active', i === index);
      step.classList.toggle('is-done', done);
      step.classList.toggle('is-locked', i > index);
      var head = $('[data-wf-step-head]', step);
      if (head) {
        head.setAttribute('data-clickable', done ? 'true' : 'false');
        head.setAttribute('aria-expanded', i === index ? 'true' : 'false');
      }
      var body = $('[data-wf-step-body]', step);
      if (body) body.setAttribute('aria-hidden', i === index ? 'false' : 'true');
    });

    this.rail.forEach(function (item, i) {
      item.setAttribute('data-state', i === index ? 'active' : (i < index ? 'done' : 'todo'));
      item.setAttribute('aria-current', i === index ? 'step' : 'false');
    });

    if (index === 1 && this.productsEl && !this.productsEl.children.length) this.addRow();
    if (index === 2) this.buildReview();

    this.summarize();
    this.hideNotice();

    // Don't yank the viewport on first paint.
    if (this._booted) scrollTo(this.steps[index]);
    this._booted = true;

    window.setTimeout(function () {
      var focusable = $('[data-wf-step].is-active input:not([disabled]), ' +
                        '[data-wf-step].is-active select:not([disabled]), ' +
                        '[data-wf-step].is-active textarea:not([disabled])', self.root);
      if (focusable && index > 0) focusable.focus({ preventScroll: true });
    }, 380);
  };

  WarrantyForm.prototype.next = function () {
    var step = this.steps[this.current];
    var bad = null;

    if (this.current === 0) {
      var radioOk = validateRadioGroup(this.form, 'Radio');
      bad = validateScope(step);
      if (!radioOk && !bad) bad = $('input[name="Radio"]', step);
      if (bad || !radioOk) return this.fail(bad);
    }

    if (this.current === 1) {
      var rows = $$('[data-wf-product]', this.productsEl);
      if (!rows.length) return this.notice('Add at least one product.');
      for (var i = 0; i < rows.length; i++) {
        var miss = validateScope(rows[i]);
        rows[i].classList.toggle('is-incomplete', !!miss);
        if (miss && !bad) bad = miss;
      }
      if (bad) return this.fail(bad);
    }

    this.go(this.current + 1);
  };

  WarrantyForm.prototype.fail = function (control) {
    this.notice('Please complete the highlighted fields before continuing.');
    if (control) {
      scrollTo(fieldOf(control) || control);
      try { control.focus({ preventScroll: true }); } catch (e) { /* noop */ }
    }
    return false;
  };

  WarrantyForm.prototype.notice = function (message) {
    if (!this.alertEl) return;
    var text = $('[data-wf-alert-text]', this.alertEl);
    if (text) text.textContent = message;
    this.alertEl.classList.add('is-visible');
  };

  WarrantyForm.prototype.hideNotice = function () {
    if (this.alertEl) this.alertEl.classList.remove('is-visible');
  };

  /* ---------- collapsed-step summaries ------------------------------------- */

  WarrantyForm.prototype.summarize = function () {
    var f = this.form;
    var v = function (name) {
      var el = f.elements[name];
      if (!el) return '';
      if (el.length && !el.value && el[0] && el[0].type === 'radio') {
        for (var i = 0; i < el.length; i++) if (el[i].checked) return el[i].value;
        return '';
      }
      return (el.value || '').trim();
    };

    var one = $('[data-wf-summary="1"]', this.root);
    if (one) {
      var name = [v('Name_First'), v('Name_Last')].filter(Boolean).join(' ');
      var bits = [name, v('Email'), v('Address_City') && v('Address_City') + ', ' + v('Address_Region')]
        .filter(Boolean);
      one.textContent = bits.length ? bits.join('  ·  ') : 'Name and contact details';
    }

    var two = $('[data-wf-summary="2"]', this.root);
    if (two) {
      var rows = this.productsEl ? $$('[data-wf-product]', this.productsEl) : [];
      var named = rows.map(function (r) {
        var m = $('[data-f="model"]', r);
        return m && m.value ? m.value : null;
      }).filter(Boolean);
      two.textContent = named.length
        ? named.length + ' product' + (named.length > 1 ? 's' : '') + ': ' + named.join(', ')
        : 'Model, problem and purchase details for each speaker';
    }
  };

  /* ---------- serialization -------------------------------------------------- */

  WarrantyForm.prototype.readRow = function (row) {
    var get = function (key) {
      var el = $('[data-f="' + key + '"]', row);
      if (!el || el.disabled) return '';
      return (el.value || '').trim();
    };
    return {
      model: get('model'),
      problem: get('problem'),
      description: get('description'),
      channel: get('channel'),
      dealerName: [get('dealer_first'), get('dealer_last')].filter(Boolean).join(' '),
      other: get('other'),
      purchaseDate: toZohoDate(get('purchase_date')),
      saleDate: toZohoDate(get('sale_date')),
      age: get('age'),
      custName: [get('cust_first'), get('cust_last')].filter(Boolean).join(' '),
      custPhone: get('cust_phone')
    };
  };

  WarrantyForm.prototype.rows = function () {
    if (!this.productsEl) return [];
    var self = this;
    return $$('[data-wf-product]', this.productsEl).map(function (r) { return self.readRow(r); });
  };

  /** Build the plain-text product block that rides along in MultiLine2. */
  WarrantyForm.prototype.flatten = function () {
    var isDealer = this.mode === 'Yes';
    var rows = this.rows();
    var out = ['=== WARRANTY PRODUCTS (' + rows.length + ') ===', ''];

    rows.forEach(function (r, i) {
      out.push('[' + (i + 1) + '] ' + (r.model || '—') + ' — ' + (r.problem || '—'));
      out.push('    Detailed description: ' + (r.description || '—'));
      if (isDealer) {
        out.push('    Sale date to customer: ' + (r.saleDate || '—'));
        out.push('    Age at failure: ' + (r.age || '—'));
        if (r.custName) out.push('    Customer name: ' + r.custName);
        if (r.custPhone) out.push('    Customer phone: ' + r.custPhone);
      } else {
        var chan = r.channel || '—';
        if (r.channel === 'Tool Dealer' && r.dealerName) chan += ' (' + r.dealerName + ')';
        if (r.channel === 'Other' && r.other) chan += ' (' + r.other + ')';
        out.push('    Purchase channel: ' + chan);
        out.push('    Approximate purchase date: ' + (r.purchaseDate || '—'));
        out.push('    Age at failure: ' + (r.age || '—'));
      }
      out.push('');
    });

    return out.join('\n');
  };

  /* ---------- review step ----------------------------------------------------- */

  WarrantyForm.prototype.buildReview = function () {
    if (!this.reviewEl) return;
    var f = this.form;
    var isDealer = this.mode === 'Yes';
    var val = function (n) { var e = f.elements[n]; return e ? (e.value || '').trim() : ''; };

    var esc = function (s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    };
    var dl = function (pairs) {
      return '<dl class="wf__dl">' + pairs.filter(function (p) { return p[1]; }).map(function (p) {
        return '<dt>' + esc(p[0]) + '</dt><dd>' + esc(p[1]).replace(/\n/g, '<br>') + '</dd>';
      }).join('') + '</dl>';
    };

    var phone = [val('PhoneNumber_countrycode'), val('PhoneNumber_first'), val('PhoneNumber_second')]
      .filter(Boolean).join('-');
    var addr = [
      val('Address_AddressLine1'),
      val('Address_AddressLine2'),
      [val('Address_City'), val('Address_Region')].filter(Boolean).join(', '),
      val('Address_ZipCode')
    ].filter(Boolean).join('\n');

    var html = '<div class="wf__review-block"><h3>Contact</h3>' + dl([
      ['Name', [val('Name_First'), val('Name_Last')].filter(Boolean).join(' ')],
      ['Email', val('Email')],
      ['Phone', phone],
      ['Tech-Life dealer', isDealer ? 'Yes' : 'No'],
      ['Shipping address', addr]
    ]) + '</div>';

    this.rows().forEach(function (r, i) {
      var pairs = [
        ['Product model', r.model],
        ['Problem category', r.problem],
        ['Description', r.description]
      ];
      if (isDealer) {
        pairs.push(['Sale date to customer', r.saleDate]);
        pairs.push(['Age at failure', r.age]);
        pairs.push(['Customer name', r.custName]);
        pairs.push(['Customer phone', r.custPhone]);
      } else {
        var chan = r.channel;
        if (r.channel === 'Tool Dealer' && r.dealerName) chan += ' (' + r.dealerName + ')';
        if (r.channel === 'Other' && r.other) chan += ' (' + r.other + ')';
        pairs.push(['Purchase channel', chan]);
        pairs.push(['Approximate purchase date', r.purchaseDate]);
        pairs.push(['Age at failure', r.age]);
      }
      html += '<div class="wf__review-block"><h3>Product ' + (i + 1) + '</h3>' + dl(pairs) + '</div>';
    });

    this.reviewEl.innerHTML = html;
  };

  /* ---------- submit ------------------------------------------------------------ */

  WarrantyForm.prototype.onSubmit = function (e) {
    var self = this;

    this.syncPhone();

    // Final guard across every step.
    var bad = null;
    for (var i = 0; i < this.steps.length; i++) {
      var miss = validateScope(this.steps[i]);
      if (miss && !bad) { bad = miss; this.go(i); break; }
    }
    if (bad || !validateRadioGroup(this.form, 'Radio')) {
      e.preventDefault();
      return this.fail(bad);
    }

    // Fold the product entries into the Zoho notes field.
    if (this.notesEl) {
      if (!this._notesLocked) this.rawNotes = this.notesEl.value || '';
      var block = this.flatten();
      this.notesEl.value = this.rawNotes
        ? block + '=== ADDITIONAL NOTES ===\n' + this.rawNotes
        : block;
      this._notesLocked = true;
    }

    // Mirror the product entries as JSON too — harmless for Zoho, useful for
    // anything downstream that wants structure instead of prose.
    var payload = $('[data-wf-payload]', this.root);
    if (payload) {
      payload.value = JSON.stringify({ dealer: this.mode === 'Yes', products: this.rows() });
    }

    if (this.submitMode !== 'iframe' || !this.sink) return; // let the browser navigate

    e.preventDefault();
    this.root.classList.add('is-submitting');
    if (this.submitBtn) this.submitBtn.setAttribute('aria-disabled', 'true');

    var done = false;
    var finish = function () {
      if (done) return;
      done = true;
      self.showSuccess();
    };

    this.sink.addEventListener('load', finish, { once: true });
    // Zoho occasionally answers with a page that blocks framing; don't hang.
    window.setTimeout(finish, 6000);

    this.form.submit();
  };

  WarrantyForm.prototype.showSuccess = function () {
    this.root.classList.remove('is-submitting');
    var shell = $('[data-wf-shell]', this.root);
    if (shell) shell.hidden = true;
    if (this.successEl) {
      this.successEl.classList.add('is-visible');
      this.successEl.setAttribute('tabindex', '-1');
      scrollTo(this.successEl);
      try { this.successEl.focus({ preventScroll: true }); } catch (e) { /* noop */ }
    }
    var live = document.getElementById('tl-live');
    if (live) live.textContent = 'Warranty ticket submitted.';
  };

  /* ---------- boot ------------------------------------------------------------- */

  function init() {
    $$('[data-warranty-form]').forEach(function (root) {
      if (root.__wf) return;
      root.__wf = new WarrantyForm(root);
    });
  }

  TL.WarrantyForm = { init: init, toZohoDate: toZohoDate };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', init);
})();
